import React, { useState, useEffect, useCallback } from 'react';
import { ClockIcon, TrophyIcon, FireIcon, XIcon } from './icons/InterfaceIcons';
import { parseTextWithImages } from './parseTextWithImages';

interface Question {
  id: string;
  text: string;
  options: string[];
  answer: string;
  difficulty: number; // 1-5
  subject: string;
}

interface GameState {
  level: number;
  score: number;
  streak: number;
  timePerQuestion: number;
  timeLeft: number;
  currentQuestion: Question | null;
  gameStatus: 'instructions' | 'playing' | 'levelUp' | 'gameOver';
  questionsAnswered: number;
}

const INITIAL_TIME = 300; // 5 minutes
const TIME_DECREASE_RATE = 15; // seconds to decrease per correct answer
const QUESTIONS_PER_LEVEL = 5;

const levelConfig = {
  1: { name: 'Beginner', color: 'text-green-600', bgColor: 'bg-green-100', maxTime: 300 },
  2: { name: 'Intermediate', color: 'text-blue-600', bgColor: 'bg-blue-100', maxTime: 240 },
  3: { name: 'Advanced', color: 'text-yellow-600', bgColor: 'bg-yellow-100', maxTime: 180 },
  4: { name: 'Expert', color: 'text-orange-600', bgColor: 'bg-orange-100', maxTime: 120 },
  5: { name: 'Genius', color: 'text-red-600', bgColor: 'bg-red-100', maxTime: 60 }
};

const motivationalQuotes = [
  "Knowledge is power, and you're getting stronger!",
  "Every question brings you closer to mastery!",
  "Your mind is your greatest weapon - keep sharpening it!",
  "Genius is 1% inspiration, 99% perspiration!",
  "The expert in anything was once a beginner!"
];

export const KnowledgeSprint: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [gameState, setGameState] = useState<GameState>({
    level: 1,
    score: 0,
    streak: 0,
    timePerQuestion: INITIAL_TIME,
    timeLeft: INITIAL_TIME,
    currentQuestion: null,
    gameStatus: 'instructions',
    questionsAnswered: 0
  });

  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const [questionPool, setQuestionPool] = useState<Question[]>([]);
  const [questionsLoaded, setQuestionsLoaded] = useState(false);

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const { supabase } = await import('../supabase');
        const { data: ndaQuestions } = await supabase.from('nda').select('*').limit(100);
        const { data: jeeQuestions } = await supabase.from('jeemain').select('*').limit(100);
        
        const allQuestions = [...(ndaQuestions || []), ...(jeeQuestions || [])].map(q => ({
          id: q.id.toString(),
          text: q.question,
          options: [q.option1, q.option2, q.option3, q.option4].filter(opt => opt),
          answer: q.type === 'Integer' ? q.correct : q[q.correct],
          difficulty: q.level || 1,
          subject: q.subject || 'General'
        }));
        
        setQuestionPool(allQuestions);
        setQuestionsLoaded(true);
      } catch (error) {
        console.error('Error loading questions:', error);
        setQuestionsLoaded(true);
      }
    };
    
    loadQuestions();
  }, []);

  const getRandomQuestion = useCallback((difficulty: number) => {
    if (questionPool.length === 0) return null;
    const filteredQuestions = questionPool.filter(q => q.difficulty === difficulty);
    return filteredQuestions[Math.floor(Math.random() * filteredQuestions.length)] || questionPool[0];
  }, [questionPool]);

  const startGame = () => {
    if (!questionsLoaded || questionPool.length === 0) {
      alert('Questions are still loading. Please wait a moment.');
      return;
    }
    const firstQuestion = getRandomQuestion(1);
    setGameState(prev => ({
      ...prev,
      gameStatus: 'playing',
      currentQuestion: firstQuestion,
      timeLeft: INITIAL_TIME
    }));
  };

  const handleAnswer = (answer: string) => {
    if (showFeedback) return;
    
    setSelectedAnswer(answer);
    const currentQuestionAnswer = gameState.currentQuestion?.answer;
    let correct = false;

    if (gameState.currentQuestion?.type === 'Integer') {
      correct = answer.trim() === currentQuestionAnswer?.trim();
    } else {
      correct = answer.trim().toLowerCase() === currentQuestionAnswer?.trim().toLowerCase();
    }
    correct = answer === gameState.currentQuestion?.answer;
    setIsCorrect(correct);
    setShowFeedback(true);

    if (correct) {
      const newStreak = gameState.streak + 1;
      const basePoints = gameState.level * 100;
      const timeBonus = Math.floor(gameState.timeLeft / 10);
      const streakBonus = newStreak >= 5 ? 100 : 0;
      const totalPoints = basePoints + timeBonus + streakBonus;

      setGameState(prev => ({
        ...prev,
        score: prev.score + totalPoints,
        streak: newStreak,
        questionsAnswered: prev.questionsAnswered + 1,
        timePerQuestion: Math.max(30, prev.timePerQuestion - TIME_DECREASE_RATE)
      }));

      setTimeout(() => {
        if ((gameState.questionsAnswered + 1) % QUESTIONS_PER_LEVEL === 0) {
          levelUp();
        } else {
          nextQuestion();
        }
      }, 2000);
    } else {
      setTimeout(() => {
        setGameState(prev => ({ ...prev, gameStatus: 'gameOver' }));
      }, 2000);
    }
  };

  const levelUp = () => {
    const newLevel = Math.min(5, gameState.level + 1);
    setGameState(prev => ({
      ...prev,
      level: newLevel,
      gameStatus: 'levelUp',
      timePerQuestion: levelConfig[newLevel].maxTime,
      timeLeft: levelConfig[newLevel].maxTime
    }));
  };

  const nextQuestion = () => {
    const nextQ = getRandomQuestion(gameState.level);
    setGameState(prev => ({
      ...prev,
      currentQuestion: nextQ,
      timeLeft: prev.timePerQuestion,
      gameStatus: 'playing'
    }));
    setSelectedAnswer(null);
    setShowFeedback(false);
  };

  const continueFromLevel = () => {
    nextQuestion();
  };

  const restartGame = () => {
    setGameState({
      level: 1,
      score: 0,
      streak: 0,
      timePerQuestion: INITIAL_TIME,
      timeLeft: INITIAL_TIME,
      currentQuestion: null,
      gameStatus: 'instructions',
      questionsAnswered: 0
    });
    setSelectedAnswer(null);
    setShowFeedback(false);
  };

  // Timer effect
  useEffect(() => {
    if (gameState.gameStatus === 'playing' && gameState.timeLeft > 0) {
      const timer = setInterval(() => {
        setGameState(prev => ({
          ...prev,
          timeLeft: prev.timeLeft - 1
        }));
      }, 1000);
      return () => clearInterval(timer);
    } else if (gameState.gameStatus === 'playing' && gameState.timeLeft <= 0) {
      setGameState(prev => ({ ...prev, gameStatus: 'gameOver' }));
    }
  }, [gameState.gameStatus, gameState.timeLeft]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const InstructionsScreen = () => (
    <div className="text-center space-y-6 max-w-2xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-brand-text-primary mb-2">⚡ The Knowledge Sprint</h1>
        <p className="text-xl text-brand-text-secondary">Can your mind outrun the clock?</p>
      </div>

      <div className="bg-brand-surface rounded-xl p-6 text-left space-y-4">
        <h2 className="text-2xl font-semibold text-brand-text-primary mb-4">🎯 How to Play</h2>
        <ul className="space-y-3 text-brand-text-secondary">
          <li className="flex items-start space-x-3">
            <span className="text-brand-primary font-bold">1.</span>
            <span>Answer questions that start easy and become progressively harder</span>
          </li>
          <li className="flex items-start space-x-3">
            <span className="text-brand-primary font-bold">2.</span>
            <span>Each question is timed - starting at 5 minutes per question</span>
          </li>
          <li className="flex items-start space-x-3">
            <span className="text-brand-primary font-bold">3.</span>
            <span>Correct answers reduce your time limit but increase your score</span>
          </li>
          <li className="flex items-start space-x-3">
            <span className="text-brand-primary font-bold">4.</span>
            <span>Wrong answer or time out = Game Over</span>
          </li>
          <li className="flex items-start space-x-3">
            <span className="text-brand-primary font-bold">5.</span>
            <span>Reach higher levels to unlock genius-level challenges!</span>
          </li>
        </ul>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-8">
        {Object.entries(levelConfig).map(([level, config]) => (
          <div key={level} className={`p-4 rounded-lg ${config.bgColor} text-center`}>
            <div className="font-bold text-lg">Level {level}</div>
            <div className={`text-sm ${config.color}`}>{config.name}</div>
            <div className="text-xs mt-1">{Math.floor(config.maxTime/60)}m {config.maxTime%60}s</div>
          </div>
        ))}
      </div>

      <button
        onClick={startGame}
        disabled={!questionsLoaded}
        className="px-8 py-4 bg-brand-primary text-white text-xl font-bold rounded-xl hover:bg-brand-primary-hover transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
      >
        {questionsLoaded ? '🚀 Start Challenge' : 'Loading Questions...'}
      </button>
    </div>
  );

  const GameScreen = () => (
    <div className="max-w-4xl mx-auto">
      {/* Game Header */}
      <div className="flex items-center justify-between mb-6 bg-brand-surface rounded-xl p-4">
        <div className="flex items-center space-x-6">
          <div className={`px-4 py-2 rounded-lg ${levelConfig[gameState.level].bgColor}`}>
            <span className="font-bold">Level {gameState.level}</span>
            <span className={`ml-2 ${levelConfig[gameState.level].color}`}>
              {levelConfig[gameState.level].name}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <TrophyIcon className="w-5 h-5 text-yellow-500" />
            <span className="font-bold text-xl">{gameState.score.toLocaleString()}</span>
          </div>
          {gameState.streak >= 5 && (
            <div className="flex items-center space-x-2 px-3 py-1 bg-orange-100 rounded-full">
              <FireIcon className="w-4 h-4 text-orange-500" />
              <span className="text-orange-700 font-semibold">{gameState.streak} streak!</span>
            </div>
          )}
        </div>
        
        <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-mono text-xl ${
          gameState.timeLeft <= 30 ? 'bg-red-100 text-red-700' :
          gameState.timeLeft <= 60 ? 'bg-yellow-100 text-yellow-700' :
          'bg-green-100 text-green-700'
        }`}>
          <ClockIcon className="w-6 h-6" />
          <span>{formatTime(gameState.timeLeft)}</span>
        </div>
      </div>

      {/* Question */}
      <div className="bg-brand-surface rounded-xl p-8 mb-6">
        <div className="mb-4">
          <span className="text-sm text-brand-text-secondary">
            Question {gameState.questionsAnswered + 1} • {gameState.currentQuestion?.subject}
          </span>
        </div>
        <div className="text-xl text-brand-text-primary mb-6">
          {parseTextWithImages(gameState.currentQuestion?.text || '')}
        </div>

        {gameState.currentQuestion?.type === 'Integer' ? (
          <div className="mb-4">
            <label className="block text-sm font-medium text-brand-text-primary mb-2">
              Enter your numerical answer:
            </label>
            <input
              type="text"
              value={selectedAnswer || ''}
              onChange={(e) => setSelectedAnswer(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAnswer(selectedAnswer || '');
              }}
              disabled={showFeedback}
              className="w-full px-4 py-3 text-lg border-2 border-brand-border rounded-lg focus:border-brand-primary focus:outline-none bg-brand-bg text-brand-text-primary"
              placeholder="Enter integer value (e.g., 42)"
            />
            <button
              onClick={() => handleAnswer(selectedAnswer || '')}
              disabled={showFeedback || !selectedAnswer}
              className="mt-4 px-6 py-3 bg-brand-primary text-white font-bold rounded-lg hover:bg-brand-primary-hover transition-colors disabled:opacity-50"
            >
              Submit Answer
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {gameState.currentQuestion?.options.map((option, index) => {
              let buttonClass = "p-4 text-left border-2 rounded-lg transition-all duration-200 ";
              
              if (showFeedback) {
                if (option === gameState.currentQuestion?.answer) {
                  buttonClass += "border-green-500 bg-green-100 text-green-800";
                } else if (option === selectedAnswer) {
                  buttonClass += "border-red-500 bg-red-100 text-red-800";
                } else {
                  buttonClass += "border-gray-300 bg-gray-50 text-gray-500";
                }
              } else if (selectedAnswer === option) {
                buttonClass += "border-brand-primary bg-brand-primary/10";
              } else {
                buttonClass += "border-brand-border hover:border-brand-primary/50 hover:bg-brand-primary/5";
              }

              return (
                <button
                  key={index}
                  onClick={() => handleAnswer(option)}
                  disabled={showFeedback}
                  className={`${buttonClass} ${!showFeedback ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="font-bold text-lg">{String.fromCharCode(65 + index)}</span>
                    <span>{parseTextWithImages(option)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {gameState.currentQuestion?.options.map((option, index) => {
            let buttonClass = "p-4 text-left border-2 rounded-lg transition-all duration-200 ";
            
            if (showFeedback) {
              if (option === gameState.currentQuestion?.answer) {
                buttonClass += "border-green-500 bg-green-100 text-green-800";
              } else if (option === selectedAnswer) {
                buttonClass += "border-red-500 bg-red-100 text-red-800";
              } else {
                buttonClass += "border-gray-300 bg-gray-50 text-gray-500";
              }
            } else if (selectedAnswer === option) {
              buttonClass += "border-brand-primary bg-brand-primary/10";
            } else {
              buttonClass += "border-brand-border hover:border-brand-primary/50 hover:bg-brand-primary/5";
            }

            return (
              <button
                key={index}
                onClick={() => handleAnswer(option)}
                disabled={showFeedback}
                className={buttonClass}
              >
                <div className="flex items-center space-x-3">
                  <span className="font-bold text-lg">{String.fromCharCode(65 + index)}</span>
                  <span>{parseTextWithImages(option)}</span>
                </div>
              </button>
            );
          })}
        </div>

        {showFeedback && (
          <div className={`mt-6 p-4 rounded-lg animate-fade-in ${
            isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            <div className="font-bold text-lg">
              {isCorrect ? '🎉 Correct!' : '❌ Incorrect!'}
            </div>
            {isCorrect && gameState.streak >= 5 && (
              <div className="text-sm mt-1">🔥 Streak bonus: +100 points!</div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const LevelUpScreen = () => (
    <div className="text-center space-y-6 max-w-2xl mx-auto">
      <div className="text-6xl mb-4">🎉</div>
      <h1 className="text-4xl font-bold text-brand-text-primary">Level {gameState.level} Unlocked!</h1>
      <div className={`inline-block px-6 py-3 rounded-xl ${levelConfig[gameState.level].bgColor}`}>
        <span className={`text-2xl font-bold ${levelConfig[gameState.level].color}`}>
          {levelConfig[gameState.level].name}
        </span>
      </div>
      
      <div className="bg-brand-surface rounded-xl p-6">
        <h3 className="text-xl font-semibold mb-4">Your Progress</h3>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-brand-primary">{gameState.score.toLocaleString()}</div>
            <div className="text-sm text-brand-text-secondary">Total Score</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">{gameState.questionsAnswered}</div>
            <div className="text-sm text-brand-text-secondary">Questions Answered</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-orange-600">{formatTime(gameState.timePerQuestion)}</div>
            <div className="text-sm text-brand-text-secondary">Time Per Question</div>
          </div>
        </div>
      </div>

      <div className="text-lg text-brand-text-secondary italic">
        "{motivationalQuotes[Math.floor(Math.random() * motivationalQuotes.length)]}"
      </div>

      <button
        onClick={continueFromLevel}
        className="px-8 py-4 bg-brand-primary text-white text-xl font-bold rounded-xl hover:bg-brand-primary-hover transition-colors cursor-pointer"
      >
        Continue Challenge
      </button>
    </div>
  );

  const GameOverScreen = () => (
    <div className="text-center space-y-6 max-w-2xl mx-auto">
      <div className="text-6xl mb-4">⏰</div>
      <h1 className="text-4xl font-bold text-brand-text-primary">Challenge Complete!</h1>
      
      <div className="bg-brand-surface rounded-xl p-6">
        <h3 className="text-xl font-semibold mb-4">Final Results</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-3xl font-bold text-brand-primary">{gameState.score.toLocaleString()}</div>
            <div className="text-sm text-brand-text-secondary">Final Score</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-blue-600">{gameState.level}</div>
            <div className="text-sm text-brand-text-secondary">Max Level</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-green-600">{gameState.questionsAnswered}</div>
            <div className="text-sm text-brand-text-secondary">Questions</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-orange-600">{gameState.streak}</div>
            <div className="text-sm text-brand-text-secondary">Best Streak</div>
          </div>
        </div>
      </div>

      <div className="flex justify-center space-x-4">
        <button
          onClick={restartGame}
          className="px-6 py-3 bg-brand-primary text-white font-bold rounded-lg hover:bg-brand-primary-hover transition-colors cursor-pointer"
        >
          Play Again
        </button>
        <button
          onClick={onClose}
          className="px-6 py-3 border border-brand-border text-brand-text-primary font-bold rounded-lg hover:bg-brand-surface transition-colors cursor-pointer"
        >
          Back to Dashboard
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-brand-bg rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">⚡</span>
              <h1 className="text-2xl font-bold text-brand-text-primary">Knowledge Sprint</h1>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-brand-surface rounded-lg cursor-pointer">
              <XIcon className="w-6 h-6" />
            </button>
          </div>

          {gameState.gameStatus === 'instructions' && <InstructionsScreen />}
          {gameState.gameStatus === 'playing' && <GameScreen />}
          {gameState.gameStatus === 'levelUp' && <LevelUpScreen />}
          {gameState.gameStatus === 'gameOver' && <GameOverScreen />}
        </div>
      </div>
    </div>
  );
};